import * as React from 'react';
import ApiClient, { getImageUrl } from '../../Services/ApiClient';
import AuthContext from '../../store/authContext';
import { useAlert } from '../ui/alertProvider';
import { useResources } from '../../store/resourcesProvider';
import { AboutImage } from '../../../Models/AboutImage';

const AboutImagesAdmin: React.FC = () => {
    const { isAdmin, isSuperAdmin } = React.useContext(AuthContext);
    const alert = useAlert();
    const resources = useResources();

    const [images, setImages] = React.useState<AboutImage[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [uploading, setUploading] = React.useState(false);
    const [file, setFile] = React.useState<File | null>(null);
    const [title, setTitle] = React.useState('');
    const [description, setDescription] = React.useState('');

    React.useEffect(() => {
        if (!isAdmin && !isSuperAdmin) return;
        load();
    }, [isAdmin, isSuperAdmin]);

    const load = async () => {
        setLoading(true);
        try {
            const data: any = await ApiClient.get('/about/images');
            setImages(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to load about images', err);
            alert.showAlert(resources.get('AboutImagesAdmin.LoadFailed','Failed to load images'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
        setFile(f);
    };

    const upload = async () => {
        if (!file) {
            alert.showAlert(resources.get('AboutImagesAdmin.SelectFile','Select a file first'), 'error');
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            if (title) fd.append('title', title);
            if (description) fd.append('description', description);
            await ApiClient.post('/about/images', fd);
            alert.showAlert(resources.get('AboutImagesAdmin.UploadSuccess', 'Image uploaded'), 'success');
            setFile(null);
            setTitle('');
            setDescription('');
            await load();
        } catch (err) {
            console.error('Upload failed', err);
            alert.showAlert(resources.get('AboutImagesAdmin.UploadFailed','Upload failed: ') + ((err as any)?.message || ''), 'error');
        } finally {
            setUploading(false);
        }
    };

    const remove = async (id: number) => {
        // open confirm modal
        setDeleteConfirm(id);
    };

    const [deleteConfirm, setDeleteConfirm] = React.useState<number | null>(null);
    const [deleteLoading, setDeleteLoading] = React.useState(false);

    const performDeleteConfirmed = async (id: number | null) => {
        if (!id) return;
        setDeleteLoading(true);
        try {
            await ApiClient.delete(`/about/images/${id}`);
            alert.showAlert(resources.get('AboutImagesAdmin.Deleted', 'Image deleted'), 'success');
            await load();
        } catch (err) {
            console.error('Delete failed', err);
            alert.showAlert(resources.get('AboutImagesAdmin.DeleteFailed','Delete failed: ') + ((err as any)?.message || ''), 'error');
        } finally {
            setDeleteLoading(false);
            setDeleteConfirm(null);
        }
    };

    const moveUp = (index: number) => {
        if (index <= 0) return;
        const arr = [...images];
        const tmp = arr[index - 1];
        arr[index - 1] = arr[index];
        arr[index] = tmp;
        setImages(arr);
    };

    const moveDown = (index: number) => {
        if (index >= images.length - 1) return;
        const arr = [...images];
        const tmp = arr[index + 1];
        arr[index + 1] = arr[index];
        arr[index] = tmp;
        setImages(arr);
    };

    if (!isAdmin && !isSuperAdmin) return <div>{resources.get('AboutImagesAdmin.NoPermission', 'No permission')}</div>;

    return (
        <div className="about-images-admin" style={{ marginTop: 24, padding: 12, border: '1px solid #ddd', borderRadius: 6 }}>
            <h3 style={{ marginTop: 0 }}>{resources.get('AboutImagesAdmin.Title', 'About Page Images')}</h3>

            <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="file" accept="image/*" onChange={onFileChange} />
                <input placeholder={resources.get('AboutImagesAdmin.TitlePlaceholder','כותרת')} value={title} onChange={e => setTitle(e.target.value)} />
                <input placeholder={resources.get('AboutImagesAdmin.DescriptionPlaceholder','פירוט')} value={description} onChange={e => setDescription(e.target.value)} />
                <button className="btn btn-primary" onClick={upload} disabled={uploading}>
                    {uploading ? resources.get('AboutImagesAdmin.Uploading', 'מעלה...') : resources.get('AboutImagesAdmin.Upload', 'העלאה')}
                </button>
                <button className="btn" onClick={load}>{resources.get('AboutImagesAdmin.Refresh', 'רענן תמונות')}</button>
            </div>

            {loading ? <div>{resources.get('AboutImagesAdmin.Loading','טוען תמונות...')}</div> : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {images.map((img, idx) => (
                        <div key={img.id} style={{ display: 'flex', gap: 12, alignItems: 'center', border: '1px solid #eee', padding: 8, borderRadius: 6 }}>
                            <div style={{ width: 120, height: 80, overflow: 'hidden', flex: '0 0 120px' }}>
                                <img
                                    src={getImageUrl(img.fileName) ?? '/voice1.jpg'}
                                    alt={img.title ?? ''}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/voice1.jpg'; }}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600 }}>{img.title ?? resources.get('AboutImagesAdmin.Untitled','(ללא כותרת)')}</div>
                                <div style={{ color: '#666' }}>{img.description}</div>
                                <div style={{ fontSize: 12, color: '#999' }}>{img.fileName}</div>
                                <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>{resources.get('AboutImagesAdmin.Owner', 'בעלים')}: {img.siteId ?? resources.get('AboutImagesAdmin.Default', '(ברירת מחדל)')}</div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <button className="btn" onClick={() => moveUp(idx)} disabled={idx === 0}>{resources.get('AboutImagesAdmin.Up','העלה')}</button>
                                <button className="btn" onClick={() => moveDown(idx)} disabled={idx === images.length - 1}>{resources.get('AboutImagesAdmin.Down','הורד')}</button>
                                <button className="btn btn-danger" onClick={() => remove(img.id)}>{resources.get('AboutImagesAdmin.Delete','מחק')}</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Mobile-friendly card list for about images */}
            <div className="cards-list">
                {images.map((img, idx) => (
                    <div key={img.id} className="item-card">
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                            <div style={{ width: 80, height: 60, overflow: 'hidden' }}>
                                <img src={getImageUrl(img.fileName) ?? '/voice1.jpg'} alt={img.title ?? ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 700 }}>{img.title}</div>
                                <div style={{ color: '#666', fontSize: 13 }}>{img.description}</div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <button className="btn" onClick={() => moveUp(idx)} disabled={idx === 0}>Up</button>
                                <button className="btn" onClick={() => moveDown(idx)} disabled={idx === images.length - 1}>Down</button>
                                <button className="btn btn-danger" onClick={() => remove(img.id)}>Delete</button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AboutImagesAdmin;